import React, { AriaAttributes } from 'react';
import classNames from 'classnames';
import { CarouselAction, CarouselActionProps } from '@components/carousel/CarouselAction';
import { Icon } from '@components/icon';
import { Body, Heading, HeadingProps } from '@components/text';
import './CarouselTitle.scss';

/**
 * Carousel title props
 *
 * @param title - Title of the carousel
 * @param size - Size of the title (small | medium | large)
 * @param numberShows - Number of shows to display next to the title (number | string)
 * @param isLive - Should the flashing live dot show
 * @param className - Additional classes to add
 */
export interface CarouselTitleProps extends Omit<HeadingProps, 'children'>, AriaAttributes {
  /** Title of the carousel */
  title: string;
  /** Size of the title (small | medium | large) */
  size?: 'small' | 'medium' | 'large';
  /** Number of shows to display next to the title (number | string) */
  numberShows?: string | number;
  /** Should the flashing live dot show */
  isLive?: boolean;
  /** (Optional) Link to page */
  href?: string;
  /** Custom component to render the title link instead of a native `<a>` (e.g. Next.js Link). Must accept `href` as a prop. */
  linkComponent?: React.ElementType;
  /** Optional action (e.g. an "Edit" link) rendered on the far right of the title row. */
  action?: CarouselActionProps;
}

/**
 * Carousel title component
 *
 * @param title - Title of the carousel
 * @param size - Size of the title (small | medium | large)
 * @param numberShows - Number of shows to display next to the title (number | string)
 * @param isLive - Should the flashing live dot show
 * @param className - Additional classes to add
 * @param href - Link to page
 * @param linkComponent - Custom component to render the title link instead of a native `<a>` (e.g. Next.js Link). Must accept `href` as a prop. Defaults to `a`.
 * @param action - Optional action (e.g. an "Edit" link) rendered on the far right of the title row.
 */
export const CarouselTitle: React.FC<CarouselTitleProps> = ({
  title,
  size = 'small',
  numberShows,
  isLive,
  className,
  href,
  linkComponent,
  action,
  ...rest
}) => {
  const LinkWrapper = linkComponent || 'a';
  const titleContent = (
    <>
      <Heading
        variant="heading-02"
        weight="semibold"
        ariaLevel={2}
        color="primary"
        size={size}
        {...rest}>
        {title}
      </Heading>
      {numberShows !== undefined && (
        <Body color="tertiary" variant="caption" size="large">
          {numberShows}
        </Body>
      )}
      {isLive && (
        <svg
          className="vds-carousel-title__live"
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="9"
          viewBox="0 0 8 9"
          fill="none">
          <path d="M4 8.5C6.20914 8.5 8 6.70914 8 4.5C8 2.29086 6.20914 0.5 4 0.5C1.79086 0.5 0 2.29086 0 4.5C0 6.70914 1.79086 8.5 4 8.5Z" />
        </svg>
      )}
      {href && (
        <div className="vds-carousel-title__arrow">
          <Icon name="chevron-forward" className="vds-carousel-title__chevron" />
        </div>
      )}
    </>
  );
  return (
    <div
      className={classNames(
        'vds-carousel-title',
        { 'vds-carousel-title--with-action': action },
        className,
      )}>
      {href ? (
        <LinkWrapper
          href={href}
          className="vds-carousel-title__link"
          aria-label={rest['aria-label'] ?? title}>
          {titleContent}
        </LinkWrapper>
      ) : (
        titleContent
      )}
      {action && <CarouselAction {...action} className="vds-carousel-title__action" />}
    </div>
  );
};
